<script lang="ts">
import { VueDraggableNext } from 'vue-draggable-next'

export default defineComponent({
  components: {
    draggable: VueDraggableNext,
  },
})
</script>
<script setup lang="ts">
import { Form, Field, ErrorMessage } from 'vee-validate'
import * as yup from 'yup'

const store = usePrestacaoDeContaStore()
const permissaoStore = usePermissaoStore()
const usuarioStore = useUsuarioStore()
const { modalEfetuarPagamento } = storeToRefs(store)
const arquivos = ref([])

// Data atual no formato YYYY-MM-DD para limitar o datepicker
const dataAtual = computed(() => {
  const hoje = new Date()
  return hoje.toISOString().split('T')[0]
})

// Data mínima permitida baseada na trava de competência
const dataMinima = computed(() => {
  try {
    // Admin sempre pode
    const isAdmin = usuarioStore.usuarioAtual.usuario?.admin === 'S'
    if (isAdmin) { return null }

    // Usuários com permissão de exclusão não são afetados pela trava
    const temPermissaoExcluir = usuarioStore.usuarioAtual.cartorio.permissoes
      .some(permissao => permissao.excluir === 1 && permissao.especialidade_nome === 'financeiro')

    if (temPermissaoExcluir) { return null }

    // Se trava desabilitada, não há restrição
    if (permissaoStore.permitirEdicaoCompetenciaFechada) { return null }

    // Se trava habilitada, não pode escolher datas de competências fechadas
    const hoje = new Date()
    const primeiroMesAtual = new Date(hoje.getFullYear(), hoje.getMonth(), 1)
    return primeiroMesAtual.toISOString().split('T')[0]
  } catch (error) {
    console.warn('Erro ao calcular data mínima:', error)
    return null
  }
})

// Validação usando Yup
const schema = computed(() => yup.object({
  data_pagamento: yup.date()
    .typeError('Data de pagamento inválida')
    .required('Data de pagamento é obrigatória')
    .test('ano-valido', 'Ano deve ter 4 dígitos', function (value) {
      if (!value) { return false }
      const ano = value.getFullYear()
      return ano >= 1000 && ano <= 9999
    })
    .test('data-minima', 'Data não permitida devido à trava de competência', function (value) {
      if (!value || !dataMinima.value) { return true }
      const dataSelecionada = new Date(value)
      const dataMinimaNormalizada = new Date(dataMinima.value)
      return dataSelecionada >= dataMinimaNormalizada
    }),
}))

const getValidationClass = (meta) => {
  if (meta.touched && meta.validated) {
    return meta.valid ? 'is-valid' : 'is-invalid'
  }
  return ''
}

function cancelar () {
  arquivos.value = []
  // Chamar reorganizarModais ANTES de resetar para manter a referência
  useStore().reorganizarModais(modalEfetuarPagamento.value)
  store.resetarModalEfetuarPagamento()
}

function atualizarArquivos (files) {
  arquivos.value = files.map(file => file.file)
}

// Função para formatar tamanho de arquivo
function formatFileSize (bytes: number): string {
  if (bytes === 0) { return '0 Bytes' }
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Função para remover arquivo
function removerArquivo (index: number) {
  arquivos.value.splice(index, 1)
}

const handleSubmit = async (values) => {
  try {
    useStore().loading = true

    console.log('Iniciando pagamento...')
    console.log('Arquivos selecionados:', arquivos.value)
    console.log('Data de pagamento:', values.data_pagamento)
    console.log('Despesa ID:', modalEfetuarPagamento.value.despesa_id)

    // Criar FormData para incluir arquivos
    const formData = new FormData()

    // Adicionar dados do pagamento
    formData.append('data_pagamento', values.data_pagamento)

    // Adicionar arquivos se existirem
    if (arquivos.value && arquivos.value.length > 0) {
      console.log(`Adicionando ${arquivos.value.length} arquivos ao FormData`)
      arquivos.value.forEach((arquivo, index) => {
        console.log(`Arquivo ${index + 1}:`, arquivo.name, arquivo.size)
        formData.append('arquivos[]', arquivo)
      })
    } else {
      console.log('Nenhum arquivo para enviar')
    }

    // Fazer a requisição usando o método upload para incluir os arquivos
    console.log('Enviando requisição...')
    const response = await useApiStore().upload(`/financeiro/despesas/${modalEfetuarPagamento.value.despesa_id}/pagar`, formData)
    console.log('Resposta da API:', response)

    // Recarregar as despesas para atualizar a lista
    await store.fetchDespesas({ competencia: useStore().competenciaFormatada })

    // Atualizar a lista de despesas filtradas do SpeedDial
    const speedDialStore = useSpeedDialStore()
    if (speedDialStore.modalDespesasFiltradas.filtroAtivo) {
      await speedDialStore.buscarDespesasFiltradas(
        speedDialStore.modalDespesasFiltradas.filtroAtivo as 'vencidas' | 'hoje' | 'proximas'
      )
    }

    arquivos.value = []
    // Chamar reorganizarModais ANTES de resetar para manter a referência
    useStore().reorganizarModais(modalEfetuarPagamento.value)
    store.resetarModalEfetuarPagamento()
    useStore().toasts.push({ content: 'Pagamento efetuado com sucesso', color: 'success' })
  } catch (err) {
    useStore().toasts.push({ content: 'Erro ao efetuar pagamento', color: 'danger' })
    console.error(err)
  } finally {
    useStore().loading = false
  }
}

</script>

<template>
  <div>
    <CModal
      id="modalEfetuarPagamento"
      :visible="modalEfetuarPagamento.exibir"
      size="lg"
      backdrop="static"
      @close="cancelar"
    >
      <CModalHeader>
        <CModalTitle id="efetuarPagamento">
          Efetuar Pagamento
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        <Form :validation-schema="schema" @submit="handleSubmit">
          <CRow class="mb-3">
            <CCol :md="6">
              <!-- Data do Pagamento -->
              <div class="mb-2">
                <label class="form-label" for="data_pagamento">
                  Data do Pagamento
                </label>
                <Field v-slot="{ field, meta }" name="data_pagamento">
                  <input
                    v-bind="field"
                    v-model="modalEfetuarPagamento.data_pagamento"
                    type="date"
                    class="form-control"
                    :class="getValidationClass(meta)"
                    :max="dataAtual"
                    :min="dataMinima"
                  >
                </Field>
                <ErrorMessage name="data_pagamento" class="invalid-feedback" />
              </div>
              <input v-model="modalEfetuarPagamento.despesa_id" type="hidden">
            </CCol>
          </CRow>

          <!-- Upload de Comprovantes -->
          <div class="mb-3">
            <h6>Comprovantes do Pagamento</h6>
            <FilePond
              ref="pond"
              name="file"
              :files="arquivos"
              :label-idle="'Arraste e solte seus arquivos ou <u>clique aqui</u>'"
              :allow-multiple="true"
              :accepted-file-types="['application/pdf', '*']"
              @updatefiles="(arquivos) => atualizarArquivos(arquivos)"
            />
            <ul v-if="arquivos.length > 0" id="listaArquivos">
              <draggable :list="arquivos" handle=".drag-handle">
                <li
                  v-for="(arquivo, index) in arquivos"
                  :key="index"
                  class="arquivo-item"
                >
                  <div class="arquivo-info">
                    <CIcon
                      icon="cil-menu"
                      class="drag-handle"
                    />
                    <span class="arquivo-nome">{{ arquivo.name }}</span>
                    <span class="arquivo-tamanho">{{ formatFileSize(arquivo.size) }}</span>
                  </div>
                  <button class="btn-remover" @click="removerArquivo(index)">
                    <CIcon icon="cil-x" size="sm" />
                  </button>
                </li>
              </draggable>
            </ul>
          </div>

          <CModalFooter>
            <CButton color="secondary" @click="cancelar">
              Cancelar
            </CButton>
            <CButton type="submit" color="primary">
              Salvar
            </CButton>
          </CModalFooter>
        </Form>
      </CModalBody>
    </CModal>
  </div>
</template>

<style scoped>
#listaArquivos {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 0.5rem;
  margin-top: 0.5rem;
  list-style: none;
}

.arquivo-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  margin-bottom: 0.25rem;
  background-color: white;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.arquivo-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.arquivo-nome {
  font-size: 0.875rem;
}

.arquivo-tamanho {
  color: #6c757d;
  font-size: 0.75rem;
}

.btn-remover {
  background: none;
  border: none;
  color: #dc3545;
  padding: 0.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-remover:hover {
  color: #bb2d3b;
}

:deep(.filepond--root){
  margin-bottom: 0;
}

:deep(.filepond--item){
  display: none !important;
}

.drag-handle {
  cursor: grab;
  color: #6c757d;
  margin-right: 0.5rem;
}

.drag-handle:active {
  cursor: grabbing;
}
</style>
